// Strength log: weight per exercise over time. Stored on users/{uid} as `lifts`.

export interface LiftEntry {
  id: string;
  d: string;     // "YYYY-MM-DD"
  kg: number;
  reps?: number; // left out when not given (Firestore rejects undefined)
}

export interface Exercise {
  id: string;
  name: string;
  bar: boolean;    // barbell: show plates per side (20 kg bar)
  created: number; // ms, keeps the list in the order exercises were added
  entries: LiftEntry[];
}

export const BAR_KG = 20;

export const newId = (): string => Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4);

const pad = (n: number) => (n < 10 ? "0" : "") + n;
export const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const parseDay = (iso: string) => new Date(`${iso}T12:00:00`);
export const dayTime = (iso: string): number => parseDay(iso).getTime();
export const fmtDay = (iso: string, withYear = false): string =>
  parseDay(iso).toLocaleDateString("sv-SE", withYear ? { day: "numeric", month: "short", year: "numeric" } : { day: "numeric", month: "short" });
export const fmtKg = (n: number): string => Number(n).toLocaleString("sv-SE", { maximumFractionDigits: 2 });

export const sortedEntries = (ex: Exercise): LiftEntry[] =>
  [...ex.entries].sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : 0));

// Entries (by id) that beat every earlier entry — the first one doesn't count
export const recordIds = (entries: LiftEntry[]): Set<string> => {
  const out = new Set<string>();
  let best = -Infinity;
  entries.forEach((e, i) => {
    if (i > 0 && e.kg > best) out.add(e.id);
    best = Math.max(best, e.kg);
  });
  return out;
};

// Plate colours follow the usual competition colours
export const PLATES: { kg: number; bg: string; fg: string }[] = [
  { kg: 25, bg: "#c8312b", fg: "#fff" },
  { kg: 20, bg: "#1f55c4", fg: "#fff" },
  { kg: 15, bg: "#e3b91b", fg: "#1a1a1a" },
  { kg: 10, bg: "#2c9b58", fg: "#fff" },
  { kg: 5, bg: "#f2f2f2", fg: "#1a1a1a" },
  { kg: 2.5, bg: "#7c858f", fg: "#fff" },
  { kg: 1.25, bg: "#b7bdc4", fg: "#1a1a1a" },
];

// Plates on each side of a 20 kg bar, heaviest first; `rest` > 0 if it doesn't add up
export const platesFor = (kg: number): { plates: typeof PLATES; rest: number } | null => {
  const side = (kg - BAR_KG) / 2;
  if (side < 0) return null;
  const plates: typeof PLATES = [];
  let rest = side;
  PLATES.forEach((p) => {
    const n = Math.floor(rest / p.kg + 1e-9);
    for (let i = 0; i < n; i++) plates.push(p);
    rest -= n * p.kg;
  });
  return { plates, rest: rest > 0.01 ? rest : 0 };
};

export const normalizeLifts = (raw: any): Exercise[] =>
  (Array.isArray(raw) ? raw : []).map((x: any) => ({
    id: String(x?.id ?? newId()),
    name: String(x?.name ?? "Övning"),
    bar: !!x?.bar,
    created: Number(x?.created) || 0,
    entries: (Array.isArray(x?.entries) ? x.entries : [])
      .filter((e: any) => e && typeof e.d === "string" && isFinite(Number(e.kg)))
      .map((e: any) => {
        const out: LiftEntry = { id: String(e.id ?? newId()), d: e.d, kg: Number(e.kg) };
        if (Number(e.reps) > 0) out.reps = Number(e.reps);
        return out;
      }),
  }));

// The log as it was in the Claude artifact (Oct 2026), imported once for the
// site owner the first time the page opens with no saved lifts.
export const SEED_LIFTS: Exercise[] = [
  { id: "benboj-djup", name: "Benböj, djup", bar: true, created: 1791822000000, entries: [{ id: "start1", d: "2026-10-12", kg: 70 }, { id: "z414jf34uf", d: "2026-10-26", kg: 70 }] },
  { id: "backsida-maskin", name: "Backsida maskin", bar: false, created: 1791822240000, entries: [{ id: "start1", d: "2026-10-12", kg: 50 }, { id: "7u8g32dkwk", d: "2026-10-26", kg: 55 }] },
  { id: "framsida-ben-maskin-zf3b", name: "Framsida ben maskin", bar: true, created: 1791822636925, entries: [{ id: "r1sn62xsw4", d: "2026-10-12", kg: 50 }] },
  { id: "vadpress", name: "Vadpress", bar: false, created: 1791822780000, entries: [{ id: "start1", d: "2026-10-12", kg: 115 }] },
  { id: "tricepsdodare", name: "Tricepsdödare", bar: false, created: 1791823200000, entries: [{ id: "start1", d: "2026-10-12", kg: 30 }, { id: "w3s6z8tcd8", d: "2026-10-26", kg: 37.5 }] },
  { id: "latsdrag", name: "Latsdrag", bar: false, created: 1791823380000, entries: [{ id: "start1", d: "2026-10-12", kg: 45 }] },
  { id: "superman", name: "Superman", bar: false, created: 1791824040000, entries: [{ id: "start1", d: "2026-10-12", kg: 0 }, { id: "9r6jd2wk9i", d: "2026-10-26", kg: 0 }] },
  { id: "hoftbojjare", name: "Höftböjjare", bar: false, created: 1791824100000, entries: [{ id: "start1", d: "2026-10-12", kg: 40 }] },
  { id: "vadpress-sittande-jnzl", name: "Vadpress sittande", bar: true, created: 1793037274410, entries: [{ id: "aicz0s3n6y", d: "2026-10-26", kg: 50 }] },
];
